import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import ArrowIcon from '@assets/icons/outline/arrow-left-to-line.svg?react';
import MenuIcon from '@assets/icons/outline/menu.svg?react';
import KnowledgeBase from './components/KnowledgeBase/KnowledgeBase';
import KnowledgeBaseIcon from '@assets/icons/question-blue-38.svg';
import { H4, SubTitle3 } from "@components/new/Typo";
import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
import NotificationsWidget from './components/NotificationsWidget/NotificationsWidget.tsx';
import UserWidget from './components/UserWidget/UserWidget.tsx';
import WalletWidget from './components/WalletWidget/WalletWidget.tsx';
import ClubWidget from './components/ClubWidget/ClubWidget.tsx';
import { Portal } from '@components/Portal';
import { Tooltip } from '@components/new/Tooltip';
import { useProfile } from '@contexts/ProfileContext';
import { useKnowledgeBase } from '@contexts/KnowledgeBaseContext';
import { getCurrentLanguage, Langs } from '@utils/lang';
import styles from './Header.module.scss';

interface IHeaderProps {
  title: string;
  description?: string;
  isSidebarCollapsed: boolean;
  switchIsSidebarCollapsed: () => void;
}

const Header = ({
  title,
  description,
  isSidebarCollapsed,
  switchIsSidebarCollapsed,
}: IHeaderProps) => {
  const { t } = useTranslation();
  const { account } = useProfile();
  const { isKnowledgeBaseVisible, setKnowledgeBaseParams } = useKnowledgeBase();
  const currentLanguage = getCurrentLanguage();

  useEffect(() => {
    if (isKnowledgeBaseVisible) {
      document.getElementById('root')?.classList.add('blurred');
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.getElementById('root')?.classList.remove('blurred');
      document.body.style.overflow = 'auto';
    };
  }, [isKnowledgeBaseVisible]);

  return (
    <div className={cn(styles.header, isSidebarCollapsed && styles.collapsed)}>
      <div className={styles.headerLeft}>
        <IconButton
          size={IconButtonSize.M}
          type={IconButtonType.SECONDARY}
          icon={isSidebarCollapsed ? MenuIcon : ArrowIcon}
          onClick={switchIsSidebarCollapsed}
        />
        <div className={styles.titleColumn}>
          <H4>{title}</H4>
          {description ? (<SubTitle3 color="grey-800">{description}</SubTitle3>) : null}
        </div>
      </div>
      <div className={cn(styles.headerActions)}>
        <NotificationsWidget />
        <WalletWidget />
        <ClubWidget />
        <UserWidget />
        {account?.region === 'CN' && currentLanguage === Langs.cn && (
          <Tooltip
            action={
              <button
                className={styles.knowledgeBaseBtn}
                onClick={() => setKnowledgeBaseParams(`https://unitrade-global.com/instructions/yandex-market-${currentLanguage}`)}
              >
                <img
                  className={styles.knowledgeBaseIcon}
                  alt="KnowledgeBaseIcon"
                  src={KnowledgeBaseIcon}
                />
              </button>
            }
            position="top"
            text={t('authenticatedLayout.btnTooltip')}
          />
        )}
      </div>
      {isKnowledgeBaseVisible && (
        <Portal>
          <KnowledgeBase/>
        </Portal>
      )}
    </div>
  );
};

export default Header;
